import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAdminRegistrationsApprovalList,
  bulkVerifyRegistrations,
  fetchTournamentsList,
} from "../../../api/tournamentControlApi";
import { getCsrfToken } from "../../../../../public/api/authApi";
import { GlobalModal } from "../../../../../global/GlobalModal";
import GlobalErrorFlashModal from "../../../../../global/GlobalErrorFlashModal";
import GlobalSuccessFlashModal from "../../../../../global/GlobalSuccessFlashModal";
import { GlobalButton } from "../../../../../global/forms/GlobalFormFields";
import GlobalBadge from "../../../../../global/GlobalBadge";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryApprovalsSection,
} from "../../../../../global/directory";
import {
  REG_COL_KEY,
  REG_PAGE_SIZE,
  REG_COLUMN_DEFS,
  REG_PICKER_COLUMN_DEFS,
  loadVis,
  regCell,
  compareReg,
  rowDateToStartOfDay,
  isTruthyBool,
  formatDateTimeCell,
} from "../tournamentApprovalsCommon";
import "../../../styles/VolunteerTable.css";
import "../../../styles/participantsList.css";
import "../../../styles/tournamentAdminApprovals.css";

const COLUMN_DEFS_FOR_FILTERS = REG_COLUMN_DEFS.map((c) => ({ id: c.id, label: c.label }));
const SORTABLE_REG_IDS = new Set(REG_COLUMN_DEFS.filter((c) => c.sortable).map((c) => c.id));

const initialRegFilters = () => ({
  student_search: "",
  column: "",
  value: "",
});

export default function TournamentRegistrationsCertificatesApprovalsSection() {
  const [tournaments, setTournaments] = useState([]);
  const [regRows, setRegRows] = useState([]);
  const [loadingReg, setLoadingReg] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [regDraft, setRegDraft] = useState(initialRegFilters);
  const [regApplied, setRegApplied] = useState(initialRegFilters);
  const [regSortColumn, setRegSortColumn] = useState("tournament_date");
  const [regSortOrder, setRegSortOrder] = useState("DESC");
  const [regColVis, setRegColVis] = useState(() => loadVis(REG_COL_KEY, REG_PICKER_COLUMN_DEFS));
  const [selectedReg, setSelectedReg] = useState(() => new Set());
  const regHeaderCb = useRef(null);
  const [busyReg, setBusyReg] = useState(false);
  const [detailReg, setDetailReg] = useState(null);
  const [regPage, setRegPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setRegApplied({ ...regDraft }), 400);
    return () => clearTimeout(t);
  }, [regDraft]);

  const loadMeta = useCallback(async () => {
    try {
      const tRes = await fetchTournamentsList({});
      setTournaments(tRes?.data || []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const selectedRegTournamentName = useMemo(() => {
    if (regApplied.column !== "tournament_name") return "";
    return String(regApplied.value || "").trim();
  }, [regApplied.column, regApplied.value]);
  const hasRegTournamentSelected = selectedRegTournamentName.length > 0;

  const loadRegistrations = useCallback(async () => {
    if (!hasRegTournamentSelected) {
      setRegRows([]);
      setSelectedReg(new Set());
      setLoadingReg(false);
      return;
    }
    setLoadingReg(true);
    setErrorMsg("");
    try {
      const res = await fetchAdminRegistrationsApprovalList({
        student_search: (regApplied.student_search || "").trim() || undefined,
      });
      setRegRows(res?.data || []);
      setSelectedReg(new Set());
    } catch (e) {
      setErrorMsg(e?.message || "Could not load registrations.");
      setRegRows([]);
    } finally {
      setLoadingReg(false);
    }
  }, [regApplied.student_search, hasRegTournamentSelected]);

  useEffect(() => {
    loadRegistrations();
  }, [loadRegistrations]);

  const upcomingRegRows = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return regRows.filter((row) => {
      const day = rowDateToStartOfDay(row.tournament_date);
      if (!day) return false;
      return day.getTime() >= today.getTime();
    });
  }, [regRows]);

  const regValueFilterOptions = useMemo(() => {
    if (!regDraft.column) return [];
    if (regDraft.column === "tournament_name") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const allowed = (tournaments || []).filter((t) => {
        const day = rowDateToStartOfDay(t.tournament_date);
        if (!day) return false;
        return day.getTime() >= today.getTime();
      });
      const seen = new Set();
      const options = [];
      for (const t of allowed) {
        const name = String(t.tournament_name || "").trim();
        if (!name || seen.has(name)) continue;
        seen.add(name);
        options.push({ value: name, label: name });
      }
      return options.sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }),
      );
    }
    const seen = new Set();
    const options = [];
    for (const row of upcomingRegRows) {
      const value = regCell(regDraft.column, row);
      if (value == null || value === "—") continue;
      const label = String(value).trim();
      if (!label || seen.has(label)) continue;
      seen.add(label);
      options.push({ value: label, label });
    }
    return options.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [regDraft.column, upcomingRegRows, tournaments]);

  const sortedReg = useMemo(() => {
    const col = REG_COLUMN_DEFS.find((c) => c.id === regSortColumn && c.sortable);
    if (!col) return upcomingRegRows;
    const mult = regSortOrder === "ASC" ? 1 : -1;
    const list = [...upcomingRegRows];
    list.sort((a, b) => mult * compareReg(a, b, regSortColumn));
    return list;
  }, [upcomingRegRows, regSortColumn, regSortOrder]);

  const filteredReg = useMemo(() => {
    const col = regApplied.column;
    const value = String(regApplied.value || "").trim();
    if (!col || !value) return sortedReg;
    const match = value.toLowerCase();
    return sortedReg.filter((row) => String(regCell(col, row) ?? "").trim().toLowerCase() === match);
  }, [sortedReg, regApplied.column, regApplied.value]);

  const pagedReg = useMemo(() => {
    const start = (regPage - 1) * REG_PAGE_SIZE;
    return filteredReg.slice(start, start + REG_PAGE_SIZE);
  }, [filteredReg, regPage]);

  const regTotalPages = Math.max(1, Math.ceil(filteredReg.length / REG_PAGE_SIZE));
  const visibleRegCols = useMemo(() => REG_COLUMN_DEFS.filter((c) => regColVis[c.id]), [regColVis]);

  useEffect(() => {
    if (regPage > regTotalPages) setRegPage(regTotalPages);
  }, [regPage, regTotalPages]);
  useEffect(() => {
    setRegPage(1);
  }, [regApplied]);

  const regPageIds = useMemo(() => pagedReg.map((r) => r.registration_id), [pagedReg]);
  const allRegSel = regPageIds.length > 0 && regPageIds.every((id) => selectedReg.has(id));
  const someRegSel = regPageIds.some((id) => selectedReg.has(id));

  useEffect(() => {
    const el = regHeaderCb.current;
    if (el) el.indeterminate = !allRegSel && someRegSel;
  }, [allRegSel, someRegSel]);

  const csrf = async () => {
    let t = localStorage.getItem("csrf-token");
    if (!t) {
      t = await getCsrfToken();
      localStorage.setItem("csrf-token", t);
    }
    return t;
  };

  const handleApproveRegs = async () => {
    const ids = Array.from(selectedReg);
    if (ids.length === 0) {
      setErrorMsg("Select at least one registration.");
      return;
    }
    setBusyReg(true);
    setErrorMsg("");
    try {
      const res = await bulkVerifyRegistrations(ids, await csrf());
      setSuccessMsg(res?.message || "Registration(s) marked verified.");
      await loadRegistrations();
    } catch (e) {
      setErrorMsg(e?.message || "Approve failed.");
    } finally {
      setBusyReg(false);
    }
  };

  const markOneRegVerified = async (registrationId) => {
    setBusyReg(true);
    setErrorMsg("");
    try {
      const res = await bulkVerifyRegistrations([registrationId], await csrf());
      setSuccessMsg(res?.message || "Registration marked verified.");
      await loadRegistrations();
    } catch (e) {
      setErrorMsg(e?.message || "Update failed.");
    } finally {
      setBusyReg(false);
    }
  };

  const toggleRegSort = useCallback(
    (columnId) => {
      const col = REG_COLUMN_DEFS.find((c) => c.id === columnId && c.sortable);
      if (!col) return;
      if (regSortColumn === columnId) setRegSortOrder((o) => (o === "ASC" ? "DESC" : "ASC"));
      else {
        setRegSortColumn(columnId);
        setRegSortOrder("ASC");
      }
    },
    [regSortColumn, regSortOrder],
  );

  const toggleAllReg = useCallback(() => {
    setSelectedReg((prev) => {
      const next = new Set(prev);
      if (allRegSel) regPageIds.forEach((id) => next.delete(id));
      else regPageIds.forEach((id) => next.add(id));
      return next;
    });
  }, [allRegSel, regPageIds]);

  const selectedRegCount = selectedReg.size;

  const showRegSelectCol = !!regColVis.__select;
  const showRegActionsCol = !!regColVis.actions;

  const regTableHeaders = useMemo(
    () => [
      ...(showRegSelectCol
        ? [
            {
              key: "__select",
              className: "checkbox-cell",
              label: (
                <input
                  ref={regHeaderCb}
                  type="checkbox"
                  aria-label="Select all"
                  checked={allRegSel}
                  onChange={toggleAllReg}
                  disabled={pagedReg.length === 0}
                />
              ),
            },
          ]
        : []),
      ...visibleRegCols.map((c) => {
        if (!SORTABLE_REG_IDS.has(c.id)) {
          return { key: c.id, label: c.label };
        }
        return {
          key: c.id,
          label: (
            <button type="button" className="vpart-sort-btn" onClick={() => toggleRegSort(c.id)}>
              {c.label}
              {regSortColumn === c.id ? (
                <span className="vpart-sort-ind" aria-hidden>
                  {regSortOrder === "ASC" ? "▲" : "▼"}
                </span>
              ) : null}
            </button>
          ),
        };
      }),
      ...(showRegActionsCol
        ? [{ key: "actions", label: "Actions", className: "td-approvals-actions-col" }]
        : []),
    ],
    [
      showRegSelectCol,
      showRegActionsCol,
      visibleRegCols,
      allRegSel,
      pagedReg.length,
      regSortColumn,
      regSortOrder,
      toggleRegSort,
      toggleAllReg,
    ],
  );

  const getRegExportData = useCallback(() => {
    const headers = visibleRegCols.map((c) => c.label);
    const rows = filteredReg.map((row) =>
      visibleRegCols.map((c) => String(regCell(c.id, row) ?? "")),
    );
    return { headers, rows };
  }, [visibleRegCols, filteredReg]);

  return (
    <>
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalErrorFlashModal message={errorMsg} onDismiss={() => setErrorMsg("")} />

      <div
        id="td-approvals-regs"
        className="user-builder-panel"
        style={{ scrollMarginTop: "2rem", marginTop: "1.5rem" }}
      >
        <div className="user-builder-panel__head">
          <h3 className="user-builder-panel__title">Registration approvals</h3>
        </div>

        <GlobalDirectoryShell
          filters={
            <GlobalDirectoryFiltersSection
              className="ub-filters ub-directory-filters td-approvals-hscroll"
              searchLabel="Search student"
              searchPlaceholder="Name, code, or phone…"
              searchValue={regDraft.student_search}
              onSearchChange={(v) => setRegDraft((f) => ({ ...f, student_search: v }))}
              columnLabel="Column"
              columnValue={regDraft.column}
              onColumnChange={(v) => setRegDraft((f) => ({ ...f, column: v, value: "" }))}
              gridColumnDefs={COLUMN_DEFS_FOR_FILTERS}
              valueLabel="Value"
              valueValue={regDraft.value}
              onValueChange={(v) => setRegDraft((f) => ({ ...f, value: v }))}
              valueOptions={regValueFilterOptions}
              onClear={() => setRegDraft(initialRegFilters())}
              clearFilterLabel="Clear filter"
              secondaryStart={
                <GlobalDirectoryColumnPicker
                  columnDefs={REG_PICKER_COLUMN_DEFS}
                  visibility={regColVis}
                  onVisibilityChange={setRegColVis}
                  storageKey={REG_COL_KEY}
                />
              }
              secondaryEnd={
                <GlobalDirectoryExportMenu
                  getExportData={getRegExportData}
                  filenameBase={`admin-tournament-registrations-approvals_${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`}
                />
              }
            />
          }
          approvals={
            <GlobalDirectoryApprovalsSection
              title="Approval & marking"
              hint={
                selectedRegCount > 0
                  ? `${selectedRegCount} selected`
                  : "Select rows, then approve registrations"
              }
              actions={
                <GlobalButton type="button" variant="primary" onClick={handleApproveRegs} loading={busyReg}>
                  Approve registrations
                </GlobalButton>
              }
            />
          }
          grid={
            <GlobalDirectoryGridSection
              loading={loadingReg}
              loadingMessage="Loading registrations…"
              headers={regTableHeaders}
              isEmpty={!loadingReg && hasRegTournamentSelected && pagedReg.length === 0}
              emptyMessage="No registrations match your filters."
              tableAriaLabel="Tournament registrations approval"
              wrapClassName="vt-table-wrap td-approvals-table-scroll"
              tableClassName="vt-table"
            >
              {!hasRegTournamentSelected ? null : (
                <>
                  {pagedReg.map((row) => (
                    <tr key={row.registration_id}>
                      {showRegSelectCol ? (
                        <td className="checkbox-cell" style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={selectedReg.has(row.registration_id)}
                            onChange={() =>
                              setSelectedReg((prev) => {
                                const next = new Set(prev);
                                if (next.has(row.registration_id)) next.delete(row.registration_id);
                                else next.add(row.registration_id);
                                return next;
                              })
                            }
                            aria-label={`Select ${row.student_name}`}
                          />
                        </td>
                      ) : null}
                      {visibleRegCols.map((col) => (
                        <td key={col.id}>
                          {col.id === "registration_verified" || col.id === "payment_verified" ? (
                            <GlobalBadge
                              active={isTruthyBool(row[col.id])}
                              activeText="Verified"
                              inactiveText="Not verified"
                            />
                          ) : col.id === "has_verified_result" ? (
                            <GlobalBadge
                              active={isTruthyBool(row.has_verified_result)}
                              activeText="Yes"
                              inactiveText="No"
                            />
                          ) : col.id === "student_name" ? (
                            <span style={{ fontWeight: 600 }}>{regCell(col.id, row)}</span>
                          ) : (
                            regCell(col.id, row)
                          )}
                        </td>
                      ))}
                      {showRegActionsCol ? (
                        <td className="td-approvals-actions">
                          <div className="td-approvals-actions-inner">
                            <GlobalButton type="button" variant="ghost" size="sm" onClick={() => setDetailReg(row)}>
                              View
                            </GlobalButton>
                            <GlobalButton
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => markOneRegVerified(row.registration_id)}
                              disabled={busyReg}
                            >
                              Mark registration verified
                            </GlobalButton>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </>
              )}
            </GlobalDirectoryGridSection>
          }
          pagination={
            <GlobalDirectoryPaginationSection
              page={regPage}
              totalPages={regTotalPages}
              total={filteredReg.length}
              pageSize={REG_PAGE_SIZE}
              onPageChange={setRegPage}
              label="Tournament registration approvals pagination"
            />
          }
        />
      </div>

      {!hasRegTournamentSelected ? (
        <p style={{ margin: "0.75rem 0 0", color: "#64748b", fontSize: "0.9375rem" }}>
          Select a tournament (column <b>Tournament</b> + value) to load this table.
        </p>
      ) : null}

      <GlobalModal
        open={detailReg != null}
        onClose={() => setDetailReg(null)}
        title="Registration"
        description={
          detailReg ? `${detailReg.student_name} — ${detailReg.tournament_name}` : undefined
        }
        size="lg"
        classNames={{
          modal: "gff-modal vpart-reg-modal",
          header: "gff-modal__header vpart-reg-modal__header",
          title: "gff-modal__title vpart-reg-modal__title",
          description: "gff-modal__description vpart-reg-modal__subtitle",
          body: "gff-modal__body vpart-reg-modal__body",
        }}
      >
        {detailReg ? (
          <div className="vpart-reg-modal__scroll">
            <article className="vpart-reg-card">
              <h3 className="vpart-reg-card__title">Student & event</h3>
              <div className="vpart-reg-fields">
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Code</span>
                  <span className="vpart-reg-field__value">{detailReg.student_code ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Contact</span>
                  <span className="vpart-reg-field__value">{detailReg.student_contact ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Membership</span>
                  <span className="vpart-reg-field__value">{detailReg.membership_name ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Student membership ID</span>
                  <span className="vpart-reg-field__value">{detailReg.student_membership_id ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Registration verified</span>
                  <span className="vpart-reg-field__value">
                    {isTruthyBool(detailReg.registration_verified) ? "Yes" : "No"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Certificate template</span>
                  <span className="vpart-reg-field__value">{detailReg.certificate_template_name ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Certificate status</span>
                  <span className="vpart-reg-field__value">{detailReg.certificate_status_name ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Payment verified</span>
                  <span className="vpart-reg-field__value">
                    {isTruthyBool(detailReg.payment_verified) ? "Yes" : "No"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Tournament result verified</span>
                  <span className="vpart-reg-field__value">
                    {isTruthyBool(detailReg.has_verified_result) ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </article>
          </div>
        ) : null}
      </GlobalModal>
    </>
  );
}
